import { apiClient } from "../client";
import { ENDPOINTS } from "../endpoints";

export type ProductCategory =
  | "hotspot" | "indoor_kits" | "outdoor_kits"
  | "accessories" | "network_solutions" | "support";

export type StockStatus = "in_stock" | "out_of_stock" | "low_stock";

export interface AdminProduct {
  _id?: string;
  name: string;
  category?: ProductCategory;
  price: number;
  currency?: string;
  availability?: boolean;
  stockStatus?: StockStatus;
  shortDescription?: string;
  fullDescription?: string;
  description?: string;
  thumbnailUrl?: string;
  images?: string[];
  rating?: number;
  reviewCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface ProductResponse {
  success?: boolean;
  message?: string;
  data?: AdminProduct | AdminProduct[];
  pagination?: { page: number; limit: number; total: number; pages: number };
}

// List all products (admin — includes inactive)
export const getAdminProducts = async (): Promise<AdminProduct[]> => {
  const res = await apiClient.get<ProductResponse>(ENDPOINTS.ADMIN.PRODUCTS);
  const d = res.data?.data;
  return Array.isArray(d) ? d : [];
};

export const getAdminProduct = async (id: string): Promise<AdminProduct | null> => {
  const res = await apiClient.get<ProductResponse>(ENDPOINTS.ADMIN.PRODUCT_DETAIL(id));
  const d = res.data?.data;
  return d && !Array.isArray(d) ? d : null;
};

export const createAdminProduct = async (payload: Partial<AdminProduct>): Promise<AdminProduct | null> => {
  const res = await apiClient.post<ProductResponse>(ENDPOINTS.ADMIN.PRODUCT_CREATE, payload);
  const d = res.data?.data;
  return d && !Array.isArray(d) ? d : null;
};

export const updateAdminProduct = async (id: string, payload: Partial<AdminProduct>): Promise<AdminProduct | null> => {
  const res = await apiClient.patch<ProductResponse>(ENDPOINTS.ADMIN.PRODUCT_UPDATE(id), payload);
  const d = res.data?.data;
  return d && !Array.isArray(d) ? d : null;
};

export const deleteAdminProduct = async (id: string): Promise<boolean> => {
  const res = await apiClient.delete<ProductResponse>(ENDPOINTS.ADMIN.PRODUCT_DELETE(id));
  return !!res.data?.success;
};

// Upload a product image file → returns the hosted URL
export const uploadProductImage = async (file: File): Promise<string | null> => {
  const form = new FormData();
  form.append("image", file);
  const res = await apiClient.post<{ success?: boolean; url?: string }>(
    ENDPOINTS.ADMIN.PRODUCT_UPLOAD_IMAGE,
    form,
    // Let the browser set multipart/form-data + boundary automatically.
    // The apiClient defaults to application/json, so we must clear it here
    // (undefined) rather than force a boundary-less multipart header.
    { headers: { "Content-Type": undefined } }
  );
  return res.data?.url || null;
};
